import type {
  BabyQuote,
  CamperQuote,
  CarQuote,
  CatQuote,
  ChildQuote,
  DogQuote,
  HouseholderQuote,
  PolicyHolder,
  Quote,
  TravelQuote,
  VanQuote,
} from "@/api_client";
import { matchProductType } from "@/utils/productUtils";
import type { ObjectId, ObjectInfo } from "../types/types";

const getObjectInfo = (
  enigmatizePolicyHolder: PolicyHolder | undefined | null,
  product: Quote,
  enigmatizedProduct: Quote | undefined,
): ObjectInfo => {
  const householderQuote = product as HouseholderQuote;
  return {
    objectId: getProductSpecificObjectId(
      enigmatizePolicyHolder,
      product,
      enigmatizedProduct,
    ),
    objectSize: {
      value: householderQuote?.householder?.livingSpace,
      unit: "m2",
    },
    objectValue: getProductSpecificObjectValue(product) ?? undefined,
    postalCode: householderQuote?.householder?.postalCode ?? "",
    year: getYear(product),
    leased: getLeased(product),
    numberOfBuildings: undefined,
    brand: getBrand(product),
  };
};

const getYear = (product: Quote): number | undefined =>
  matchProductType(product, {
    householder: undefined,
    baby: undefined,
    travel: undefined,
    car: (carQuote) =>
      (carQuote as CarQuote)?.carDetails?.modelYear ?? undefined,
    income: undefined,
    sport: undefined,
    child: undefined,
    cat: undefined,
    dog: undefined,
    van: (vanQuote) =>
      (vanQuote as VanQuote)?.vanDetails?.manufactureYear ?? undefined,
    camper: (camperQuote) =>
      (camperQuote as CamperQuote)?.camperDetails?.manufactureYear ?? undefined,
    "": undefined,
  });

const getLeased = (product: Quote): boolean | undefined =>
  matchProductType(product, {
    householder: undefined,
    baby: undefined,
    travel: undefined,
    car: (carQuote) =>
      (carQuote as CarQuote)?.isOwnedByFinanceCompany ?? undefined,
    income: undefined,
    sport: undefined,
    child: undefined,
    cat: undefined,
    dog: undefined,
    van: (vanQuote) =>
      (vanQuote as VanQuote)?.isOwnedByFinanceCompany ?? undefined,
    camper: (camperQuote) =>
      (camperQuote as CamperQuote)?.isOwnedByFinanceCompany ?? undefined,
    "": undefined,
  });

const getBrand = (product: Quote): string | undefined =>
  matchProductType(product, {
    householder: undefined,
    baby: undefined,
    travel: undefined,
    car: (carQuote) => (carQuote as CarQuote)?.carDetails?.brand ?? undefined,
    income: undefined,
    sport: undefined,
    child: undefined,
    cat: undefined,
    dog: undefined,
    van: (vanQuote) => (vanQuote as VanQuote)?.vanDetails?.brand ?? undefined,
    camper: (camperQuote) =>
      (camperQuote as CamperQuote)?.camperDetails?.brand ?? undefined,
    "": undefined,
  });

const getProductSpecificObjectId = (
  enigmatizedPolicyHolder: PolicyHolder | undefined | null,
  product: Quote,
  enigmatizedProduct: Quote | undefined,
) => {
  const quoteForMatch = enigmatizedProduct || product;

  return matchProductType<ObjectId>(quoteForMatch, {
    householder: () => getHouseholderObjectId(product),
    baby: (enigmatizedBabyQuote) =>
      getBabyObjectId(enigmatizedPolicyHolder, enigmatizedBabyQuote),
    travel: (enigmatizedTravelQuote) =>
      getTravelObjectId(enigmatizedPolicyHolder, enigmatizedTravelQuote),
    car: (enigmatizedCarQuote) => getCarObjectId(enigmatizedCarQuote),
    income: {
      id: undefined,
      type: "",
    },
    sport: {
      id: undefined,
      type: "",
    },
    van: (enigmatizedVanQuote) => getVanObjectId(enigmatizedVanQuote),
    camper: (enigmatizedCamperQuote) =>
      getCamperObjectId(enigmatizedCamperQuote),
    child: (enigmatizedChildQuote) => getChildObjectId(enigmatizedChildQuote),
    cat: (enigmatizedCatQuote) => getCatObjectId(enigmatizedCatQuote),
    dog: (enigmatizedDogQuote) => getDogObjectId(enigmatizedDogQuote),
    "": {
      id: undefined,
      type: "",
    },
  });
};

const getProductSpecificObjectValue = (product: Quote) =>
  matchProductType(product, {
    householder: undefined,
    baby: undefined,
    travel: undefined,
    car: undefined,
    income: undefined,
    sport: undefined,
    child: undefined,
    cat: (catQuote) => catQuote?.catCovered?.compensationLimit,
    dog: (dogQuote) => dogQuote?.dogCovered?.compensationLimit,
    van: undefined,
    camper: undefined,
    "": undefined,
  });

const getBabyObjectId = (
  enigmatizePolicyHolder: PolicyHolder | undefined | null,
  enigmatizedProduct: BabyQuote | undefined,
) => {
  const id =
    enigmatizedProduct?.babyMother?.ssn ?? enigmatizePolicyHolder?.ssn ?? "";
  return { type: "SSN", id };
};

const getTravelObjectId = (
  enigmatizePolicyHolder: PolicyHolder | undefined | null,
  enigmatizedProduct: TravelQuote | undefined,
) => {
  const id =
    enigmatizedProduct?.coveredPerson?.familyMember?.ssn ??
    enigmatizePolicyHolder?.ssn ??
    "";
  return { type: "SSN", id };
};

const getHouseholderObjectId = (product: Quote) => {
  const householderQuote = product as HouseholderQuote;
  return {
    type: "OTH",
    id: householderQuote.householder?.livingSpace?.toString() ?? "",
  };
};

const getChildObjectId = (enigmatizedProduct?: ChildQuote) => ({
  type: "SSN",
  id: enigmatizedProduct?.coveredPerson?.ssn ?? "",
});

const getCarObjectId = (enigmatizedProduct?: CarQuote) => ({
  type: "REG",
  id: enigmatizedProduct?.registrationNumber ?? "",
});

const getCamperObjectId = (enigmatizedProduct?: CamperQuote) => ({
  type: "REG",
  id: enigmatizedProduct?.registrationNumber ?? "",
});

const getVanObjectId = (enigmatizedProduct?: VanQuote) => ({
  type: "REG",
  id: enigmatizedProduct?.registrationNumber ?? "",
});

const getCatObjectId = (enigmatizedProduct?: CatQuote) => ({
  type: "ANAM",
  id: enigmatizedProduct?.catCovered?.catName ?? "",
});

const getDogObjectId = (enigmatizedProduct?: DogQuote) => ({
  type: "ANAM",
  id: enigmatizedProduct?.dogCovered?.dogName ?? "",
});

export default {
  getObjectInfo,
};
